import { getFormDetail, listForms } from '@/lib/data-client/forms'
import {
  clearCustomerListPageCacheSession,
  resetCustomerListPageCacheForLogin,
} from '@/lib/customer-list-page-cache'
import { loadFixture } from '@/lib/mock-api/persistent-store'
import { sampleDashboardStats } from '@/lib/mock-data'
import type { ApiEnvelope, SessionUser } from '@/types/api'
import type { DashboardStats, Form, PublicForm, ResponseSummary } from '@/types/form'

export type InternalLoginKind = 'sales' | 'admin'

export type InternalLoginConfig = {
  enabled: boolean
  loginSales: boolean
  loginAdmin: boolean
  loginSuper: boolean
  mode: 'development' | 'preview' | 'production'
}

export type PublicFormAccessResult = {
  allowed: boolean
  code:
    | 'login_required'
    | 'authenticated_session_allowed'
    | 'public_form_login_check_allowed'
    | 'public_form_login_check_denied'
    | 'public_form_login_check_failed'
    | 'public_form_login_check_not_configured'
    | 'public_form_login_check_skipped'
    | 'public_form_login_check_token_not_configured'
    | 'public_form_role_not_allowed'
    | 'preview_login_check_skipped'
    | 'super_admin_public_form_blocked'
  reason?: string
  message?: string
}

export const disabledInternalLoginConfig: InternalLoginConfig = {
  enabled: false,
  loginSales: false,
  loginAdmin: false,
  loginSuper: false,
  mode: 'production',
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  })

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status}`)
  }

  const envelope = (await response.json()) as ApiEnvelope<T>
  return envelope.data
}

export const api = {
  async me(): Promise<SessionUser> {
    try {
      const user = await request<SessionUser>('/api/auth/me')
      resetCustomerListPageCacheForLogin(user)
      return user
    } catch {
      const demoUser = getDemoSessionUser()
      if (demoUser) {
        resetCustomerListPageCacheForLogin(demoUser)
        return demoUser
      }
      throw new Error('Auth provider not configured. Use Google Login or an internal login route.')
    }
  },
  async dashboardStats(): Promise<DashboardStats> {
    try {
      return await request<DashboardStats>('/api/dashboard/stats')
    } catch {
      return sampleDashboardStats
    }
  },
  async forms(): Promise<Form[]> {
    try {
      return await request<Form[]>('/api/forms')
    } catch {
      return (await listForms({ limit: 100 })).data
    }
  },
  async form(formId: string): Promise<PublicForm> {
    try {
      return await request<PublicForm>(`/api/forms/${formId}`)
    } catch {
      return getFormDetail(formId)
    }
  },
  async publicForm(formId: string): Promise<PublicForm> {
    try {
      return await request<PublicForm>(`/api/public/forms/${formId}`)
    } catch {
      return getFormDetail(formId)
    }
  },
  async responses(formId: string): Promise<ResponseSummary[]> {
    try {
      return await request<ResponseSummary[]>(`/api/forms/${formId}/responses`)
    } catch {
      return loadFixture().responses.filter((response) => response.formId === formId)
    }
  },
  async logout() {
    try {
      await request('/api/auth/logout', { method: 'POST' })
    } catch {
      // Local demo sessions still need client-side cleanup when the API is unavailable.
    } finally {
      localStorage.removeItem('miaoform_user')
      localStorage.removeItem('miaoform_demo_user')
      clearCustomerListPageCacheSession()
    }
  },
  async internalLoginConfig(): Promise<InternalLoginConfig> {
    try {
      return await request<InternalLoginConfig>('/api/auth/internal-login-config')
    } catch {
      return disabledInternalLoginConfig
    }
  },
  async internalLogin(input: {
    kind: InternalLoginKind
    account: string
    password: string
  }): Promise<SessionUser> {
    const user = await request<SessionUser>('/api/auth/internal-login', {
      method: 'POST',
      body: JSON.stringify(input),
    })
    resetCustomerListPageCacheForLogin(user)
    return user
  },
  async superAdminLogin(input: { account: string; password: string }): Promise<SessionUser> {
    const user = await request<SessionUser>('/api/auth/super-login', {
      method: 'POST',
      body: JSON.stringify(input),
    })
    resetCustomerListPageCacheForLogin(user)
    return user
  },
  async publicFormAccess(formId: string): Promise<PublicFormAccessResult> {
    const response = await fetch(
      `/api/auth/public-form-access?formId=${encodeURIComponent(formId)}`,
      {
        headers: { Accept: 'application/json' },
      },
    )

    if (response.status === 401) {
      return {
        allowed: false,
        code: 'login_required',
        reason: 'session_missing_or_expired',
      }
    }

    if (!response.ok) {
      return {
        allowed: false,
        code: 'public_form_login_check_failed',
        reason: `http_${response.status}`,
      }
    }

    const envelope = (await response.json()) as ApiEnvelope<PublicFormAccessResult>
    return envelope.data
  },
}

function getDemoSessionUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem('miaoform_demo_user')
    if (!raw) return null
    return JSON.parse(raw) as SessionUser
  } catch {
    return null
  }
}
